/**
 * Per-tab active account — the source of truth for "which account is THIS tab
 * showing", stored in `sessionStorage`.
 *
 * ── Why sessionStorage and not the URL ──────────────────────────────────────
 * The previous scheme encoded the active account as a POSITIONAL index in the
 * URL path (`/u/<n>`, Gmail-style). That made every copyable URL carry a
 * device-local coordinate: a link copied from the address bar pointed at
 * "whatever account sits at slot n on the recipient's device" — silently the
 * WRONG account when the recipient happened to have enough accounts for the
 * index to be in range, and a stale pointer for the sender as soon as any
 * account was removed (indices shift). See
 * `infra/MULTIACCOUNT_URL_OPTIONS.md` for the full analysis.
 *
 * `sessionStorage` is per-tab BY DEFINITION and survives a reload, which is
 * exactly the lifetime the active account needs — so the URL goes back to
 * naming RESOURCES only, and stays portable.
 *
 * ── Two invariants this module exists to hold ───────────────────────────────
 * 1. **Synchronous reads.** `getAuthToken` / the `X-Active-Account` header
 *    resolver, the session machine's module-init reconcile and the PowerSync
 *    provider's prime all resolve the active account BEFORE any await is
 *    possible. Resolving it asynchronously is what caused the historical
 *    "visitor flip → DB thrash" boot bug. `sessionStorage.getItem` is
 *    synchronous, so that property is preserved unchanged.
 * 2. **We store the `userId`, not a position.** An id keeps meaning when the
 *    registry mutates, which deletes the whole class of positional bugs (and
 *    with it the `tab-account-pin.ts` workaround the index scheme needed).
 *
 * ── Reactivity ──────────────────────────────────────────────────────────────
 * {@link activeTabAccountId} is a router-agnostic, app-lifetime reactive
 * accessor (same shape as the `reactive-location.ts` singleton it replaces): it
 * must work for consumers mounted ABOVE `<RouterProvider>` (the PowerSync
 * provider, `useSession().userId` consumed by `theme-provider`), where the
 * router's `useLocation()` throws.
 *
 * It invalidates on THREE events, not one:
 *   • {@link TAB_ACCOUNT_CHANGED_EVENT} — this tab switched account;
 *   • the DOM `storage` event for the registry key — ANOTHER tab mutated the
 *     shared registry (sign-out / remove), which can make this tab's pinned
 *     userId disappear;
 *   • {@link ACCOUNTS_CHANGED_EVENT} — a same-tab registry mutation.
 * The last two matter because the RESOLVED account can change while this tab's
 * stored id stays byte-identical, so a plain signal over the id alone would not
 * notify. Hence the revision counter.
 *
 * Note that sessionStorage deliberately does NOT propagate across tabs: a
 * switch in one tab must never move another tab, which is the whole point of
 * per-tab accounts.
 */

import { createSignal } from "solid-js";
import { accountIdForAlias } from "@/lib/account-alias";

/** sessionStorage key holding THIS tab's active account userId. */
export const TAB_ACCOUNT_STORAGE_KEY = "tab_account";

/**
 * localStorage key remembering the last account activated on this DEVICE.
 * Only ever used to seed a FRESH tab (one with no sessionStorage entry yet):
 * it is a default, never an override of a tab's own choice.
 */
export const LAST_ACTIVE_ACCOUNT_KEY = "last_active_account";

/** Same-tab notification that this tab's active account changed. */
export const TAB_ACCOUNT_CHANGED_EVENT = "tab-account-changed";

/** Registry-changed event (mirrors the constant in `lib/accounts.ts`). */
const ACCOUNTS_CHANGED_EVENT = "accounts-changed";

/** localStorage key of the shared accounts registry. */
const ACCOUNTS_STORAGE_KEY = "accounts";

/**
 * Query parameter carrying the ONE-SHOT account handoff into a new tab.
 *
 * A tab opened with ctrl+click / `target="_blank" rel="noopener"` does NOT
 * inherit `sessionStorage`, so without this it would land on the device's
 * default account rather than the one the link was clicked from. The app's own
 * "open in new tab" affordances append `?as=<alias>`; the new tab consumes it
 * on its first account read and strips it from the URL immediately, so the
 * address bar is canonical again within the same tick.
 *
 * The value is an opaque DEVICE-LOCAL alias, never a userId — see
 * `lib/account-alias.ts` for why.
 */
export const ACCOUNT_HANDOFF_PARAM = "as";

/** Module-level latch: the handoff is consumed exactly once per document. */
let handoffConsumed = false;

/**
 * Consumes a `?as=<alias>` handoff, if present, into this tab's pin and strips
 * the parameter from the URL via `history.replaceState` (no navigation, no
 * history entry — the canonical URL simply replaces the decorated one).
 *
 * Idempotent and self-latching, and called from {@link readTabAccountId} rather
 * than from an entry-point module, so it cannot be defeated by import order:
 * whoever resolves the active account FIRST — the `X-Active-Account` header
 * resolver, the session machine's module-init reconcile, the PowerSync prime —
 * triggers it before reading.
 *
 * An unknown alias (a link that travelled to another device) is ignored and
 * still stripped: the tab falls back to its default account.
 */
export function consumeAccountHandoff(): void {
  if (handoffConsumed) {
    return;
  }
  handoffConsumed = true;
  let alias: string | null = null;
  let url: URL;
  try {
    url = new URL(globalThis.location.href);
    alias = url.searchParams.get(ACCOUNT_HANDOFF_PARAM);
  } catch {
    return;
  }
  if (alias === null) {
    return;
  }
  const userId = accountIdForAlias(alias);
  if (userId !== null) {
    try {
      globalThis.sessionStorage.setItem(TAB_ACCOUNT_STORAGE_KEY, userId);
    } catch {
      // Storage unavailable — fall through; the tab uses its default account.
    }
    rememberLastActiveAccountId(userId);
  }
  // Strip the parameter whether or not it resolved, so a stale/foreign alias
  // never lingers in a URL the user may copy.
  try {
    url.searchParams.delete(ACCOUNT_HANDOFF_PARAM);
    globalThis.history.replaceState(globalThis.history.state, "", url.toString());
  } catch {
    // No History API — the parameter stays, but it is inert.
  }
  notifyTabAccountChanged();
}

/**
 * Synchronous read of THIS tab's active account userId, or `null` when the tab
 * has not resolved one yet (a fresh tab, before {@link writeTabAccountId}).
 * Tolerant of storage being unavailable (private mode, disabled cookies).
 */
export function readTabAccountId(): string | null {
  consumeAccountHandoff();
  try {
    const raw = globalThis.sessionStorage.getItem(TAB_ACCOUNT_STORAGE_KEY);
    return raw === null || raw === "" ? null : raw;
  } catch {
    return null;
  }
}

/**
 * Pins THIS tab to `userId` and remembers it as the device-wide default for
 * future fresh tabs. Dispatches {@link TAB_ACCOUNT_CHANGED_EVENT} so same-tab
 * reactive consumers (the DB binding, the session view) re-resolve without a
 * reload — the in-app switch path.
 *
 * ── `silent` — the guest-upgrade ordering trap ──────────────────────────────
 * The guest→real merge needs the two sides of "who is active" to move at
 * DIFFERENT times:
 *   • the PULL side (the synchronous credential/selector resolver) must already
 *     say REAL, so the flush's authenticated push is accepted;
 *   • the PUSH side (the reactive PowerSync DB binding) must NOT move yet, or it
 *     swaps away from the guest database while we are still draining it — and
 *     the merged rows never reach the server.
 *
 * `silent: true` writes the pin without notifying, which is exactly the shape
 * the previous URL-based scheme had for free (it wrote a plain localStorage
 * default that nothing was subscribed to). Callers using it MUST call
 * {@link notifyTabAccountChanged} once the critical section is over.
 */
export function writeTabAccountId(userId: string, options?: { silent?: boolean }): void {
  try {
    globalThis.sessionStorage.setItem(TAB_ACCOUNT_STORAGE_KEY, userId);
  } catch {
    // Storage unavailable — the tab still works, it just falls back to the
    // default account on reload.
  }
  rememberLastActiveAccountId(userId);
  if (options?.silent !== true) {
    notifyTabAccountChanged();
  }
}

/** Clears this tab's pin (used when its account is removed). */
export function clearTabAccountId(): void {
  try {
    globalThis.sessionStorage.removeItem(TAB_ACCOUNT_STORAGE_KEY);
  } catch {
    // Nothing to clear.
  }
  notifyTabAccountChanged();
}

/** Persists the device-wide "last activated account" default. */
export function rememberLastActiveAccountId(userId: string): void {
  try {
    globalThis.localStorage.setItem(LAST_ACTIVE_ACCOUNT_KEY, userId);
  } catch {
    // Best-effort only.
  }
}

/** Reads the device-wide "last activated account" default. */
export function readLastActiveAccountId(): string | null {
  try {
    const raw = globalThis.localStorage.getItem(LAST_ACTIVE_ACCOUNT_KEY);
    return raw === null || raw === "" ? null : raw;
  } catch {
    return null;
  }
}

/**
 * Emits the same-tab change event (no-op without a DOM), making every reactive
 * consumer re-resolve which account this tab shows. Exported for the callers of
 * `writeTabAccountId(..., { silent: true })`, which must emit it themselves once
 * their critical section is done.
 */
export function notifyTabAccountChanged(): void {
  try {
    globalThis.dispatchEvent(new Event(TAB_ACCOUNT_CHANGED_EVENT));
  } catch {
    // No DOM (SSR / worker) — nothing to notify.
  }
}

// ─── Reactive accessor (app-lifetime singleton) ─────────────────────────────

let revisionAccessor: (() => number) | null = null;

/**
 * Subscribes the calling reactive scope to every event that can change which
 * account this tab resolves to. Returns the revision so the read is tracked.
 *
 * `equals: false` makes every bump notify even though the counter value is
 * irrelevant — we only care that dependents re-run.
 */
function trackTabAccountChanges(): number {
  if (!revisionAccessor) {
    const [revision, setRevision] = createSignal(0, { equals: false });
    const bump = () => {
      setRevision((r) => r + 1);
    };
    if (typeof globalThis.addEventListener === "function") {
      globalThis.addEventListener(TAB_ACCOUNT_CHANGED_EVENT, bump);
      globalThis.addEventListener(ACCOUNTS_CHANGED_EVENT, bump);
      // Cross-tab registry mutation: another tab signed out / removed an
      // account, which can invalidate the account THIS tab is pinned to.
      globalThis.addEventListener("storage", (event) => {
        const { key } = event as StorageEvent;
        if (key === ACCOUNTS_STORAGE_KEY || key === null) {
          bump();
        }
      });
    }
    revisionAccessor = revision;
  }
  return revisionAccessor();
}

/**
 * REACTIVE accessor of this tab's active account userId. Re-runs dependents on
 * an in-tab switch AND on any registry mutation (same-tab or cross-tab), since
 * both can change which account this tab actually resolves to.
 *
 * Safe to call from anywhere — inside or outside the router tree.
 */
export function activeTabAccountId(): string | null {
  trackTabAccountChanges();
  return readTabAccountId();
}
